import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectScopeVisualizer = ({ onScopeComplete }) => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [projectComplexity, setProjectComplexity] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  const services = [
    {
      id: 'web-dev',
      name: '3D Web Development',
      icon: 'Code',
      basePrice: 15000,
      timeline: '6-8 weeks',
      description: 'Interactive 3D websites with WebGL'
    },
    {
      id: 'portfolio',
      name: 'Portfolio Design',
      icon: 'Briefcase',
      basePrice: 8000,
      timeline: '3-4 weeks',
      description: 'Custom portfolio websites'
    },
    {
      id: 'branding',
      name: 'Brand Identity',
      icon: 'Palette',
      basePrice: 12000,
      timeline: '4-6 weeks',
      description: 'Complete brand identity systems'
    },
    {
      id: 'ui-ux',
      name: 'UI/UX Design',
      icon: 'Smartphone',
      basePrice: 10000,
      timeline: '4-5 weeks',
      description: 'User interface and experience design'
    },
    {
      id: 'animation',
      name: '3D Animation',
      icon: 'Play',
      basePrice: 6000,
      timeline: '2-3 weeks',
      description: 'Custom 3D animations and interactions'
    },
    {
      id: 'consultation',
      name: 'Creative Direction',
      icon: 'Lightbulb',
      basePrice: 5000,
      timeline: '2-4 weeks',
      description: 'Strategic creative guidance'
    }
  ];

  const complexityLevels = [
    {
      id: 'simple',
      name: 'Simple',
      multiplier: 1,
      description: 'Basic features, standard design',
      icon: 'Circle'
    },
    {
      id: 'moderate',
      name: 'Moderate',
      multiplier: 1.5,
      description: 'Custom features, unique design',
      icon: 'Square'
    },
    {
      id: 'complex',
      name: 'Complex',
      multiplier: 2,
      description: 'Advanced features, cutting-edge tech',
      icon: 'Hexagon'
    }
  ];

  const steps = [
    { title: 'Select Services', description: 'Choose what you need' },
    { title: 'Set Complexity', description: 'Define project scope' },
    { title: 'Review Estimate', description: 'See timeline & budget' }
  ];

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev?.includes(serviceId) 
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateEstimate = () => {
    const selectedServiceData = services?.filter(s => selectedServices?.includes(s?.id));
    const complexity = complexityLevels?.find(c => c?.id === projectComplexity);
    
    if (!complexity || selectedServiceData?.length === 0) return null;

    const basePrice = selectedServiceData?.reduce((sum, service) => sum + service?.basePrice, 0);
    const totalPrice = Math.round(basePrice * complexity?.multiplier);
    
    const maxTimeline = Math.max(...selectedServiceData?.map(s => parseInt(s?.timeline?.split('-')?.[1])));
    const adjustedTimeline = Math.round(maxTimeline * complexity?.multiplier);

    return {
      services: selectedServiceData,
      complexity,
      totalPrice,
      timeline: `${Math.max(4, adjustedTimeline - 2)}-${adjustedTimeline} weeks`,
      breakdown: selectedServiceData?.map(s => ({
        ...s,
        adjustedPrice: Math.round(s?.basePrice * complexity?.multiplier)
      }))
    };
  };

  const estimate = calculateEstimate();

  const nextStep = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onScopeComplete({
        selectedServices,
        projectComplexity,
        estimate
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedServices?.length > 0;
      case 1: return projectComplexity !== '';
      case 2: return estimate !== null;
      default: return false;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-panel rounded-2xl p-8 max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center cosmic-glow">
            <Icon name="Calculator" size={24} className="text-white" />
          </div>
          <div>
            <h3 className="font-headline text-2xl text-foreground">Project Scope Visualizer</h3>
            <p className="font-body text-muted-foreground">Get an instant estimate for your project</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          {steps?.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                index <= currentStep 
                  ? 'bg-accent text-background' :'bg-surface text-muted-foreground'
              }`}>
                {index + 1}
              </div>
              <div className="ml-2 text-left">
                <p className={`font-body text-sm ${index <= currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step?.title}
                </p>
                <p className="font-body text-xs text-muted-foreground">{step?.description}</p>
              </div>
              {index < steps?.length - 1 && (
                <div className={`w-8 h-px mx-4 ${index < currentStep ? 'bg-accent' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Step Content */}
      <AnimatePresence mode="wait">
        {currentStep === 0 && (
          <motion.div
            key="services"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h4 className="font-headline text-lg text-foreground mb-4">Select Services</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services?.map((service) => (
                <motion.div
                  key={service?.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedServices?.includes(service?.id)
                      ? 'border-accent bg-accent/10' :'border-border bg-surface/30 hover:border-accent/50'
                  }`}
                  onClick={() => toggleService(service?.id)}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Icon name={service?.icon} size={20} className={selectedServices?.includes(service?.id) ? 'text-accent' : 'text-muted-foreground'} />
                    <h5 className="font-body font-medium text-foreground">{service?.name}</h5>
                  </div>
                  <p className="font-body text-sm text-muted-foreground mb-2">{service?.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="font-body text-sm text-accent">From ${service?.basePrice?.toLocaleString()}</span>
                    <span className="font-body text-xs text-muted-foreground">{service?.timeline}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {currentStep === 1 && (
          <motion.div
            key="complexity"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h4 className="font-headline text-lg text-foreground mb-4">Project Complexity</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {complexityLevels?.map((level) => (
                <motion.div
                  key={level?.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                    projectComplexity === level?.id
                      ? 'border-accent bg-accent/10' :'border-border bg-surface/30 hover:border-accent/50'
                  }`}
                  onClick={() => setProjectComplexity(level?.id)}
                >
                  <Icon 
                    name={level?.icon} 
                    size={32} 
                    className={`mx-auto mb-3 ${projectComplexity === level?.id ? 'text-accent' : 'text-muted-foreground'}`} 
                  />
                  <h5 className="font-headline text-lg text-foreground mb-2">{level?.name}</h5>
                  <p className="font-body text-sm text-muted-foreground mb-3">{level?.description}</p>
                  <span className="font-body text-sm text-accent">×{level?.multiplier} multiplier</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {currentStep === 2 && estimate && (
          <motion.div
            key="estimate"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h4 className="font-headline text-lg text-foreground mb-4">Project Estimate</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass-panel-light rounded-lg p-6">
                <h5 className="font-headline text-lg text-foreground mb-4">Investment Range</h5>
                <div className="text-center">
                  <div className="text-3xl font-headline text-accent mb-2">
                    ${(estimate?.totalPrice * 0.8)?.toLocaleString()} - ${(estimate?.totalPrice * 1.2)?.toLocaleString()}
                  </div>
                  <p className="font-body text-sm text-muted-foreground">Based on {estimate?.complexity?.name?.toLowerCase()} complexity</p>
                </div>
              </div>

              <div className="glass-panel-light rounded-lg p-6">
                <h5 className="font-headline text-lg text-foreground mb-4">Timeline</h5>
                <div className="text-center">
                  <div className="text-3xl font-headline text-accent mb-2">{estimate?.timeline}</div>
                  <p className="font-body text-sm text-muted-foreground">From project kickoff</p>
                </div>
              </div>
            </div>

            <div className="glass-panel-light rounded-lg p-6">
              <h5 className="font-headline text-lg text-foreground mb-4">Service Breakdown</h5>
              <div className="space-y-3">
                {estimate?.breakdown?.map((service) => (
                  <div key={service?.id} className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <Icon name={service?.icon} size={16} className="text-muted-foreground" />
                      <span className="font-body text-foreground">{service?.name}</span>
                    </div>
                    <span className="font-body text-accent">${service?.adjustedPrice?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <p className="font-body text-sm text-foreground">
                <Icon name="Info" size={16} className="inline mr-2 text-accent" />
                This is an estimated range. Final pricing will be determined after discussing your specific requirements and project details.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Navigation */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 0}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="text-center">
          <p className="font-body text-sm text-muted-foreground">
            Step {currentStep + 1} of {steps?.length}
          </p>
        </div>

        <Button
          variant="default"
          onClick={nextStep}
          disabled={!canProceed()}
          iconName={currentStep === steps?.length - 1 ? "Check" : "ArrowRight"}
          iconPosition="right"
        >
          {currentStep === steps?.length - 1 ? 'Complete' : 'Next'}
        </Button>
      </div>
    </motion.div>
  );
};

export default ProjectScopeVisualizer;